import { Box, Button, IconButton, Paper, Typography } from "@mui/material";
import { QRCodeCanvas } from "qrcode.react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IoIosRemoveCircleOutline, IoMdCheckmarkCircleOutline } from "react-icons/io";
import { LuFactory, LuFileDown, LuLock, LuLockOpen, LuNewspaper, LuPlus, LuQrCode } from "react-icons/lu";
import { MdOutlineRadioButtonUnchecked } from "react-icons/md";
import { RiDownloadCloud2Line, RiFileUploadLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import foot from "../../../assets/logo/foot.png";
import { fetchWithToken, useAuth } from "../../../authentication/AuthProvider";
import RefreshButton from "../../../components/button/RefreshButton";
import { MainContainer } from "../../../components/container/MainContainer";
import { TableHeaderContainer } from "../../../components/container/TableHeaderContainer";
import { TableHeaderToolsContainer } from "../../../components/container/TableHeaderToolsContainer";
import { TableTitleContainer } from "../../../components/container/TableTitleContainer";

import CheckBox from "../../../components/input/CheckBox";
import TextBox from "../../../components/input/TextBox";
import { useContextProvider } from "../../../components/page-context/ContextProvider";
import SegmentedTabs from "../../../components/tabs/SegmentedTabs";
import config from "../../../config";
import { DPP } from "../../../db_access/dpp";
import { EnvFactor } from "../../../db_access/env_factor";
import { Project } from "../../../db_access/project";
import EvidenceRepository from "../../../organizational-page-components/EvidenceRepository";
import routing from "../../../routing";
import { importWorkspacePhotoToEvidence } from "../../Products/DPP/importWorkspacePhoto";
import MaterialCompositionGrid from "../../Products/DPP/MaterialCompositionGrid";
import VisitLogGrid from "../../Products/DPP/VisitLogGrid";
import ProductDPP_CreateForm from "../../Products/DPP/ProductDPP_CreateForm";
import { dedupeMaterials } from "../../Products/DPP/dedupeMaterials";
import { getDppFormat } from "../../Products/DPP/ProductDPP_Format";
import ProductDPP_Preview from "../../Products/DPP/ProductDPP_Preview";

// Project's identity fields are keyed project_id/project_name (not the raw
// product_id/product_name) — see getDppFormat in ProductDPP_Format.js.
const dpp_format = getDppFormat("project");

// A list item that still matches its format's blank template exactly — i.e.
// the placeholder row every list field starts with — rather than something
// the user (or a prior load) actually filled in. Compares against the
// format's actual default[0] template object (not itemDef's per-field
// defaults, which can drift out of sync with it) so it stays correct
// regardless of any such mismatch.
const isBlankListItem = (item, template) => !!template && Object.keys(template).every((key) => (item?.[key] ?? template[key]) === template[key]);

// dpp_format/dppPassportSections are shared verbatim with the Product module,
// so their labels/blurbs are hardcoded to say "Product". Word-boundary regex
// (not a plain substring replace) so words like "Production Date"/"Production
// Facility" aren't corrupted into "Projection Date/Facility".
const renameProductToProject = (str) => {
  if (typeof str !== "string") return str;
  return str
    .replace(/\bProducts\b/g, "Projects")
    .replace(/\bProduct\b/g, "Project")
    .replace(/\bproducts\b/g, "projects")
    .replace(/\bproduct\b/g, "project");
};
const ProjectDPP_Edit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedProjects, enqueueAlert } = useContextProvider();
  const fileInputRef = useRef(null);
  const create = { label: "Create", value: "Create" };
  const preview = { label: "Preview", value: "Preview" };
  const publishTab = { label: "Publish", value: "Publish" };
  const evidence = { label: "Evidence Repository", value: "Evidence Repository" };
  const tabs = [create, preview, publishTab, evidence];
  const [activeTab, setActiveTab] = useState(create.value);
  useEffect(() => {
    if (selectedProjects.length !== 1) {
      navigate(routing.sector.projects.dashboard);
    }
  }, [selectedProjects, navigate]);
  const [dpp, setDPP] = useState(
    dpp_format.reduce((acc, item) => {
      acc[item.field] = item.default;
      return acc;
    }, {})
  );
  const [sectionEditability, setSectionEditability] = useState(
    [...new Set(dpp_format.map((item) => item.section))].reduce((acc, section) => {
      acc[section] = true;
      return acc;
    }, {})
  );
  const isFieldEditable = (field) => {
    const format = dpp_format.find((f) => f.field === field);
    return format ? sectionEditability[format.section] ?? true : true;
  };
  const [photo, setPhoto] = useState();
  const [photoPreview, setPhotoPreview] = useState();
  const [photoRefreshSignal, setPhotoRefreshSignal] = useState(0);

  const baseUrl = `${window.location.protocol}//${window.location.host}`;
  const { user } = useAuth();
  const isCAdmin = user.is_company_admin === 1;
  const buttonCustomize = (label, Icon, onClick) => (
    <Button
      onClick={onClick}
      sx={{
        color: "#166534",
        textTransform: "none",
        "&:hover": { backgroundColor: "rgba(23, 163, 74, 0.2)" },
        fontFamily: "Poppins",
      }}
      startIcon={<Icon />}>
      {t(label)}
    </Button>
  );
  const savePhoto = (dpp_id) => {
    const fd = new FormData();
    fd.append("id", dpp_id);
    fd.append("photo", photo);
    DPP.save_photo(fd);
  };
  const [isPublishing, setIsPublishing] = useState(false);
  const publish = () => {
    if (isPublishing) return;
    setIsPublishing(true);
    DPP.new_published(dpp, "project")
      .then((res) => res.json())
      .then((data) => {
        if (data["dpp_id"]) {
          const dpp_id = data["dpp_id"];
          setDPP((i) => ({ ...i, _id: dpp_id }));
          if (selectedProjects.length === 1) {
            savePhoto(dpp_id);
          }
        }
      })
      .finally(() => setIsPublishing(false));
  };
  const buttonPublish = () => buttonCustomize(isPublishing ? (dpp._id ? "Republishing..." : "Publishing...") : dpp._id ? "Republish" : "Publish", LuNewspaper, publish);
  const pending = () => {
    DPP.new_pending({ ...dpp, id: selectedProjects[0]["id"] }, "project")
      .then((res) => res.json())
      .then((data) => {
        if (data["dpp_id"]) {
          const dpp_id = data["dpp_id"];
          setDPP((i) => ({ ...i, _id: dpp_id }));
          if (selectedProjects.length === 1) {
            savePhoto(dpp_id);
          }
        }
      });
  };
  const buttonPending = () => buttonCustomize("Pending", LuNewspaper, pending);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const handleDownloadPdf = async () => {
    if (isDownloadingPdf) return;
    setIsDownloadingPdf(true);
    try {
      const { generateDppPdf } = await import("../../Products/DPP/generateDppPdf");
      await generateDppPdf(`${dpp.project_name || "digital-project-passport"}.pdf`);
    } finally {
      setIsDownloadingPdf(false);
    }
  };
  const buttonDownloadPdf = () => buttonCustomize(isDownloadingPdf ? "Generating..." : "Download PDF", LuFileDown, handleDownloadPdf);
  const loadPhotoFromArivu = () => {
    return Project.get_photo(dpp["id"])
      .then((res) => {
        if (res.ok) {
          return res.blob();
        } else {
          return undefined;
        }
      })
      .then(async (data) => {
        setPhoto(data);
        if (data && selectedProjects[0]?.id) {
          await importWorkspacePhotoToEvidence(data, selectedProjects[0]["id"], "project_id");
          setPhotoRefreshSignal((s) => s + 1);
        }
      });
  };
  const loadMaterialsFromArivu = () => {
    return EnvFactor.get_materials_by_project_id(selectedProjects[0]["id"])
      .then((res) => res.json())
      .then((data) => {
        const materials = data.map((d) => ({
          type: d.type,
          category: d.category,
          description: d.description,
          quantity: d.quantity,
          unit: d.unit,
          gwp: d.gwp_total,
          biogenic_gwp: d.biogenic_gwp,
          _source: "arivu",
        }));
        const materialsTemplate = dpp_format.find((f) => f.field === "materials").default[0];
        setDPP((prev) => ({
          ...prev,
          // Drop any rows from a previous "Load from Arivu" click (tagged _source)
          // before appending the fresh fetch, so re-clicking replaces rather than
          // duplicating. Rows the user added manually (no _source tag) are kept.
          materials: [
            ...prev.materials.filter((m) => !isBlankListItem(m, materialsTemplate) && m._source !== "arivu"),
            ...materials,
          ],
        }));
      });
  };
  const [isLoadingArivu, setIsLoadingArivu] = useState(false);
  const loadFromArivu = () => {
    if (isLoadingArivu) return;
    setIsLoadingArivu(true);
    const mainLoad = Project.get_by_id(selectedProjects[0]["id"])
      .then((res) => res.json())
      .then((data) =>
        setDPP((prev) => {
          const project = data[0];
          const passport = {
            project_id: project.id,
            mci: project.mci,
            gwp_total: project.gwp,
            manufacturer_address: project.detail_address,
            manufacturer_email: project.detail_email,
            manufacturer_phone: project.detail_phone,
            manufacturer_contact_person: project.detail_contact_person,
            service_life: project.service_life,
            extended_service_life: project.extended_service_life,
            place_of_manufacture: project.place_of_manufacture,
            place_of_use: project.place_of_use,
            project_name: project.name,
          };
          const updated = {};
          const appended = {};

          for (const key in passport) {
            if (isFieldEditable(key)) {
              updated[key] = passport[key];
            } else {
              const format = dpp_format.find((f) => f.field === key);
              if (format && format.type === "list") {
                appended[key] = passport[key];
              }
            }
          }
          for (const key in appended) {
            const prevList = Array.isArray(prev[key]) ? prev[key] : [];
            const template = dpp_format.find((f) => f.field === key)?.default?.[0];
            const cleanedPrevList = prevList.filter((item) => !isBlankListItem(item, template));
            updated[key] = [...cleanedPrevList, ...appended[key]];
          }
          return {
            ...prev,
            ...updated,
          };
        })
      );
    Promise.all([mainLoad, loadPhotoFromArivu(), loadMaterialsFromArivu()])
      .then(() => enqueueAlert({ message: "Data Loaded Successfully", severity: "success" }))
      .finally(() => setIsLoadingArivu(false));
  };
  const buttonLoadFromArivu = () => buttonCustomize(isLoadingArivu ? "Loading..." : "Load from Arivu", RiDownloadCloud2Line, loadFromArivu);

  const loadFromPDF = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const buttonLoadFromPDF = () => buttonCustomize(isLoadingPdf ? "Loading..." : "Load from PDF", RiFileUploadLine, loadFromPDF);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    event.target.value = "";
    if (!file || file.type !== "application/pdf") {
      return;
    }
    setIsLoadingPdf(true);
    const url = `${config.apiUrl}/api/dpp/pdf_to_passport_json_endpoint`;
    const body = new FormData();
    body.append("file", file);
    fetchWithToken(url, {
      ...config.method.POST,
      body,
    })
      .then((res) => res.json())
      .then((data) => {
        const passport = data["data"];
        const materials_json = passport["materials_json"];
        const materials = typeof materials_json === "string" ? JSON.parse(materials_json) : materials_json;
        passport["materials"] = materials;
        const certifications_json = passport["certifications_json"];
        const certifications = typeof certifications_json === "string" ? JSON.parse(certifications_json) : certifications_json;
        passport["certifications"] = certifications;
        setDPP((prev) => {
          const updated = {};
          const appended = {};

          for (const key in passport) {
            if (isFieldEditable(key)) {
              updated[key] = passport[key];
            } else {
              const format = dpp_format.find((f) => f.field === key);
              if (format && format.type === "list") {
                appended[key] = passport[key];
              }
            }
          }
          for (const key in appended) {
            const prevList = Array.isArray(prev[key]) ? prev[key] : [];
            const template = dpp_format.find((f) => f.field === key)?.default?.[0];
            const cleanedPrevList = prevList.filter((item) => !isBlankListItem(item, template));
            updated[key] = [...cleanedPrevList, ...appended[key]];
          }
          return {
            ...prev,
            ...updated,
          };
        });
        enqueueAlert({ message: "Data Loaded Successfully", severity: "success" });
      })
      .finally(() => setIsLoadingPdf(false));
  };
  const onChange = (e, field, index = undefined, subField = undefined) => {
    const attribute = dpp_format.find((d) => d.field === field);
    if (["string"].includes(attribute.type)) {
      setDPP((p) => ({ ...p, [field]: e.target.value }));
    } else if (["float"].includes(attribute.type)) {
      setDPP((p) => ({ ...p, [field]: parseFloat(e.target.value) }));
    } else if (["boolean"].includes(attribute.type)) {
      setDPP((p) => ({ ...p, [field]: e.target.checked }));
    } else if (["list"].includes(attribute.type)) {
      const subAttribute = attribute.itemDef.find((d) => d.field === subField);
      setDPP((p) => ({
        ...p,
        [field]: p[field].map((item, i) => {
          if (i === index) {
            if (["string", "float"].includes(subAttribute.type)) {
              return { ...item, [subField]: e.target.value };
            } else if (["boolean"].includes(subAttribute.type)) {
              return { ...item, [subField]: e.target.checked };
            }
          }
          return item;
        }),
      }));
    }
  };
  const buttonLock = (visible, onClick) => {
    return (
      <Box sx={{ display: visible ? "block" : "none" }}>
        <IconButton
          onClick={onClick}
          sx={{
            "&:hover": { backgroundColor: "transparent" },
            padding: 1,
            color: "black",
          }}>
          <LuLock />
        </IconButton>
      </Box>
    );
  };
  const buttonUnlock = (visible, onClick) => {
    return (
      <Box sx={{ display: visible ? "block" : "none" }}>
        <IconButton
          onClick={onClick}
          sx={{
            "&:hover": { backgroundColor: "transparent", color: "black" },
            padding: 1,
            color: "lightgray",
          }}>
          <LuLockOpen />
        </IconButton>
      </Box>
    );
  };
  const buttonSectionLockUnlock = (section) => {
    const editable = sectionEditability[section] ?? true;
    const setEditable = (value) => {
      setSectionEditability((prev) => ({ ...prev, [section]: value }));
    };
    return (
      <Box>
        {buttonLock(!editable, () => setEditable(true))}
        {buttonUnlock(editable, () => setEditable(false))}
      </Box>
    );
  };
  const dpp_control = (section) => {
    const editable = sectionEditability[section] ?? true;
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {dpp_format
          .filter((d) => d.section === section)
          .map((d, i) => {
            if (["string", "float"].includes(d.type)) {
              return (
                <Box
                  key={i}
                  sx={{
                    minWidth: "300px",
                    display: "flex",
                    alignItems: "flex-end",
                  }}>
                  <TextBox label={renameProductToProject(d.label)} value={dpp[d.field]} onChange={(e) => onChange(e, d.field)} disabled={d.disabled || !editable} placeholder={renameProductToProject(d.label)} />
                </Box>
              );
            }
            if (["boolean"].includes(d.type)) {
              return (
                <Box
                  key={i}
                  sx={{
                    minWidth: "300px",
                    display: "flex",
                    alignItems: "flex-end",
                  }}>
                  <CheckBox
                    label={renameProductToProject(d.label)}
                    checked={dpp[d.field]}
                    onChange={(e) => onChange(e, d.field)}
                    Icon={MdOutlineRadioButtonUnchecked}
                    CheckIcon={IoMdCheckmarkCircleOutline}
                    disabled={!editable}
                  />
                </Box>
              );
            }
            if (d.field === "materials") {
              return (
                <MaterialCompositionGrid
                  key={i}
                  label={d.label}
                  itemDef={d.itemDef}
                  items={dpp[d.field]}
                  editable={editable}
                  renameLabel={renameProductToProject}
                  onChange={(newItems) => setDPP((p) => ({ ...p, [d.field]: newItems }))}
                />
              );
            }
            if (["list"].includes(d.type)) {
              const newItem = () => {
                const defaultItem = d.itemDef.reduce((acc, def) => {
                  acc[def.field] = def.default;
                  return acc;
                }, {});
                setDPP((p) => ({
                  ...p,
                  [d.field]: [...p[d.field], defaultItem],
                }));
              };
              const itemDef = d.itemDef;
              return (
                <Box key={i} sx={{ width: 1 }}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    {dpp[d.field].map((m, mi) => (
                      <Box
                        key={mi}
                        sx={{
                          display: "flex",
                        }}>
                        <IconButton
                          disabled={!editable}
                          sx={{
                            marginTop: 2.5,
                            marginBottom: "auto",
                            color: "red",
                          }}
                          onClick={() => {
                            setDPP((p) => ({
                              ...p,
                              [d.field]: p[d.field].filter((item, index) => index !== mi),
                            }));
                          }}>
                          <IoIosRemoveCircleOutline />
                        </IconButton>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                          }}>
                          {itemDef.map((def, defindex) => {
                            if (def.computed) {
                              const totalQty = dpp[d.field].reduce((s, item) => s + (parseFloat(item.quantity) || 0), 0);
                              const qty = parseFloat(m.quantity) || 0;
                              const pct = totalQty > 0 ? (qty / totalQty * 100).toFixed(1) : "0.0";
                              return (
                                <Box key={defindex} sx={{ minWidth: "260px" }}>
                                  <TextBox label={renameProductToProject(def.label)} value={pct} onChange={() => {}} placeholder={renameProductToProject(def.label)} />
                                </Box>
                              );
                            }
                            if (["string", "float"].includes(def.type)) {
                              return (
                                <Box key={defindex} sx={{ minWidth: "260px" }}>
                                  <TextBox label={renameProductToProject(def.label)} value={m[def.field]} onChange={(e) => onChange(e, d.field, mi, def.field)} disabled={!editable} placeholder={renameProductToProject(def.label)} />
                                </Box>
                              );
                            }
                            if (["boolean"].includes(def.type)) {
                              return (
                                <Box
                                  key={defindex}
                                  sx={{
                                    minWidth: "300px",
                                    alignItems: "flex-end",
                                    display: "flex",
                                  }}>
                                  <CheckBox
                                    label={renameProductToProject(def.label)}
                                    checked={m[def.field]}
                                    onChange={(e) => onChange(e, d.field, mi, def.field)}
                                    Icon={MdOutlineRadioButtonUnchecked}
                                    CheckIcon={IoMdCheckmarkCircleOutline}
                                    disabled={!editable}
                                  />
                                </Box>
                              );
                            }
                            return null;
                          })}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ paddingY: 1.5 }} />
                  <Box sx={{ display: "flex", opacity: editable ? 1 : 0.5, pointerEvents: editable ? "auto" : "none" }}>
                    {buttonCustomize(`${t("Add")} ${t(renameProductToProject(d.label))}`, LuPlus, newItem)}
                  </Box>
                </Box>
              );
            }
            return null;
          })}
      </Box>
    );
  };
  const section_title = (title, Icon, iconColor) => {
    return (
      <TableHeaderContainer>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            padding: 1,
          }}>
          <Icon color={iconColor} size={20} />
          <Typography
            sx={{
              color: "black",
              fontFamily: "Poppins",
              fontSize: "22px",
              fontWeight: 500,
            }}>
            {t(renameProductToProject(title))}
          </Typography>
        </Box>
        <Box>{buttonSectionLockUnlock(title)}</Box>
      </TableHeaderContainer>
    );
  };
  const getPassport = () => {
    DPP.get_published(selectedProjects[0]["id"], "project")
      .then((res) => res.json())
      .then((data) => {
        if (data.length !== 0) {
          const passport = data[0];
          setDPP((i) => ({ ...i, ...passport, materials: dedupeMaterials(passport.materials) }));
        }
      });
  };
  const getPhoto = () => {
    DPP.get_photo(dpp._id)
      .then((res) => (res.ok ? res.blob() : undefined))
      .then((f) => setPhoto(f));
  };
  useEffect(() => {
    if (selectedProjects.length > 0) {
      const p = selectedProjects[0];
      const temp = { ...dpp, project_name: p?.name, id: p?.id };
      setDPP(temp);
    }
  }, [selectedProjects]);
  useEffect(() => {
    if (selectedProjects.length !== 1) {
      return;
    }
    getPassport();
  }, [selectedProjects]);
  useEffect(() => {
    if (dpp._id) {
      getPhoto();
    }
  }, [dpp._id]);
  useEffect(() => {
    if (photo) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(photo);
    } else {
      setPhotoPreview(undefined);
    }
  }, [photo]);
  return (
    <MainContainer>
      <Box sx={{ display: selectedProjects.length === 1 ? "block" : "none" }}>
        <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 3 }}>
          <Box sx={{ width: "100%", maxWidth: "800px" }}>
            <SegmentedTabs options={tabs} selected={activeTab} setSelected={setActiveTab} />
          </Box>
        </Box>

        <Box sx={{ display: activeTab === create.value ? "block" : "none" }}>
          <Box sx={{ mb: 2, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1.5 }}>
            {buttonLoadFromArivu()}
            {buttonLoadFromPDF()}
          </Box>
          <ProductDPP_CreateForm
            dpp={dpp}
            dppControl={dpp_control}
            entityId={selectedProjects[0]?.id}
            qrValue={`${baseUrl}${routing.digital_passport.view_published}/${dpp.id}`}
            idColumnName="project_id"
            renameLabel={renameProductToProject}
            photoRefreshSignal={photoRefreshSignal}
            onPhotosChanged={() => setPhotoRefreshSignal((s) => s + 1)}
            nameField="project_name"
          />
          <input ref={fileInputRef} type="file" accept="application/pdf" onChange={handleFileChange} style={{ display: "none" }} />
        </Box>

        <Box sx={{ display: activeTab === preview.value ? "block" : "none", gap: 2, paddingY: 2 }}>
          <ProductDPP_Preview dpp={dpp} photoPreview={photoPreview} entityId={selectedProjects[0]?.id} renameLabel={renameProductToProject} photoRefreshSignal={photoRefreshSignal} dppFormat={dpp_format} nameField="project_name" />
        </Box>

        <Box sx={{ display: activeTab === publishTab.value ? "block" : "none", gap: 2, paddingY: 2 }}>
          <Box sx={{ display: "flex", gap: 1.5, mb: 3 }}>
            {buttonPublish()}
            {buttonPending()}
            {dpp._id && buttonDownloadPdf()}
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "stretch" }}>
          <Paper sx={{ padding: 3, flex: 1, minWidth: 320, maxWidth: 500 }}>
            {section_title("Hosting", LuFactory, "#2cbea5")}
            {dpp_control("Hosting")}
          </Paper>
          <Paper sx={{ padding: 3, flex: 1, minWidth: 320, maxWidth: 500, display: "flex", flexDirection: "column" }}>
            {section_title("QR Code", LuQrCode, "#2cbea5")}
            {dpp._id ? (
              <Box sx={{ mt: 2, flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2 }}>
                <Typography sx={{ fontWeight: 600, fontSize: "18px", fontFamily: "Poppins", textAlign: "center" }}>{dpp.project_name}</Typography>
                <QRCodeCanvas
                  value={`${baseUrl}${routing.digital_passport.view_published}/${selectedProjects[0]?.id}`}
                  size={256} // Size in pixels
                  bgColor="#ffffff" // Background color
                  fgColor="#000000" // Foreground color
                  level="H" // Error correction level: 'L', 'M', 'Q', 'H'
                  marginSize={10}
                  imageSettings={{
                    src: foot,
                    x: undefined,
                    y: undefined,
                    height: 24,
                    width: 24,
                    opacity: 1,
                    excavate: true,
                  }}
                />
              </Box>
            ) : (
              <Typography sx={{ mt: 2, color: "#94a3b8", fontFamily: "Poppins" }}>
                {t("Publish or mark this DPP as Pending to generate a QR code.")}
              </Typography>
            )}
          </Paper>
          </Box>
          {dpp._id && (
            <Paper variant="Table Container" sx={{ mt: 2 }}>
              <TableHeaderContainer>
                <TableTitleContainer>
                  <Typography variant="Table Title">{t("QR Scan Activity")}</Typography>
                </TableTitleContainer>
                <TableHeaderToolsContainer>
                  <RefreshButton />
                </TableHeaderToolsContainer>
              </TableHeaderContainer>
              <VisitLogGrid entityId={selectedProjects[0]?.id} entityType="project" />
            </Paper>
          )}
          {dpp._id && (
            <Box sx={{ position: "absolute", left: "-9999px", top: 0, width: "800px" }}>
              <ProductDPP_Preview
                dpp={dpp}
                entityId={selectedProjects[0]?.id}
                fullMode
                qrValue={`${baseUrl}${routing.digital_passport.view_published}/${selectedProjects[0]?.id}`}
                renameLabel={renameProductToProject}
                photoRefreshSignal={photoRefreshSignal}
                dppFormat={dpp_format}
                nameField="project_name"
              />
            </Box>
          )}
        </Box>

        <Box sx={{ display: activeTab === evidence.value ? "block" : "none", gap: 2, paddingY: 2 }}>
          <EvidenceRepository
            entityId={selectedProjects[0]?.id}
            idColumnName="project_id"
            scope="DPP Evidence"
            extraRows={(dpp.certifications || [])
              .filter((c) => c.url)
              .map((c, i) => ({
                id: `cert-${i}`,
                name: c.name || `${t("Certificate")} ${i + 1}`,
                url: c.url,
                uploaded_at: c.issueDate,
              }))}
          />
        </Box>
      </Box>
    </MainContainer>
  );
};
export default ProjectDPP_Edit;
